import { iconBehavior } from '@fluentui/accessibility';
import * as customPropTypes from '@fluentui/react-proptypes';
import { getElementType, getUnhandledProps, useAccessibility, useStyles, useTelemetry } from '@fluentui/react-bindings';
import { callable } from '@fluentui/styles';
import * as PropTypes from 'prop-types';
import * as React from 'react'; // @ts-ignore

import { ThemeContext } from 'react-fela';
import { createShorthandFactory, commonPropTypes } from '../../utils';
import { withSafeTypeForAs } from '../../types';

var Icon = function Icon(props) {
  var context = React.useContext(ThemeContext);

  var _useTelemetry = useTelemetry(Icon.displayName, context.telemetry),
      setStart = _useTelemetry.setStart,
      setEnd = _useTelemetry.setEnd;

  setStart();
  var accessibility = props.accessibility,
      alt = props.alt,
      ariaLabel = props['aria-label'],
      bordered = props.bordered,
      circular = props.circular,
      className = props.className,
      color = props.color,
      disabled = props.disabled,
      design = props.design,
      name = props.name,
      outline = props.outline,
      rotate = props.rotate,
      size = props.size,
      styles = props.styles,
      variables = props.variables,
      xSpacing = props.xSpacing;
  var _context$theme$icons = context.theme.icons,
      icons = _context$theme$icons === void 0 ? {} : _context$theme$icons;
  var maybeIcon = icons[name];
  var isSvgIcon = maybeIcon && maybeIcon.isSvg;
  var getA11Props = useAccessibility(accessibility, {
    debugName: Icon.displayName,
    mapPropsToBehavior: function mapPropsToBehavior() {
      return {
        alt: alt,
        'aria-label': ariaLabel
      };
    },
    rtl: context.rtl
  });

  var _useStyles = useStyles(Icon.displayName, {
    className: Icon.className,
    mapPropsToStyles: function mapPropsToStyles() {
      return {
        bordered: bordered,
        circular: circular,
        color: color,
        disabled: disabled,
        // name is required only for font icons
        // one can compose the Icon component with FontIcon to handle this if necessary
        name: isSvgIcon ? undefined : name,
        outline: outline,
        rotate: rotate,
        size: size,
        xSpacing: xSpacing,
        isFontIcon: !isSvgIcon,
        isSvgIcon: isSvgIcon
      };
    },
    mapPropsToInlineStyles: function mapPropsToInlineStyles() {
      return {
        className: className,
        design: design,
        styles: styles,
        variables: variables
      };
    },
    rtl: context.rtl
  }),
      classes = _useStyles.classes;

  var ElementType = getElementType(props);
  var unhandledProps = getUnhandledProps(Icon.handledProps, props);
  var element = React.createElement(ElementType, getA11Props('root', Object.assign({
    className: classes.root
  }, unhandledProps)), isSvgIcon && callable(maybeIcon.icon)({
    classes: classes,
    rtl: context.rtl,
    props: props
  }));
  setEnd();
  return element;
};

Icon.className = 'ui-icon';
Icon.displayName = 'Icon';
Icon.defaultProps = {
  as: 'span',
  accessibility: iconBehavior,
  size: 'medium',
  rotate: 0
};
Icon.propTypes = Object.assign({}, commonPropTypes.createCommon({
  children: false,
  content: false,
  color: true
}), {
  bordered: PropTypes.bool,
  circular: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  outline: PropTypes.bool,
  rotate: PropTypes.number,
  size: customPropTypes.size,
  xSpacing: PropTypes.oneOf(['none', 'before', 'after', 'both'])
});
Icon.handledProps = Object.keys(Icon.propTypes);
Icon.create = createShorthandFactory({
  Component: Icon,
  mappedProp: 'name',
  allowsJSX: false
});
/**
 * An Icon displays a pictogram with semantic meaning.
 */

export default withSafeTypeForAs(Icon);